import { useState, useEffect } from 'react';

export default function MeetingTimer({ startTime, onTimeWarning }) {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showTimer, setShowTimer] = useState(true);
  const [hasWarned30, setHasWarned30] = useState(false);
  const [hasWarned60, setHasWarned60] = useState(false);

  useEffect(() => {
    if (!startTime) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = Math.floor((now - startTime) / 1000); // seconds
      setElapsedTime(elapsed);

      // Notifications at 30 min and 60 min
      if (elapsed >= 1800 && !hasWarned30) {
        setHasWarned30(true);
        if (onTimeWarning) {
          onTimeWarning(30);
        }
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('QuickMeet - 30 Minutes', {
            body: 'Your meeting has been going for 30 minutes',
            icon: '/logo.png'
          });
        }
      }

      if (elapsed >= 3600 && !hasWarned60) {
        setHasWarned60(true);
        if (onTimeWarning) {
          onTimeWarning(60);
        }
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('QuickMeet - 1 Hour', {
            body: 'Your meeting has been going for 1 hour. Consider taking a break!',
            icon: '/logo.png'
          });
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime, hasWarned30, hasWarned60, onTimeWarning]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerColor = () => {
    if (elapsedTime < 1800) return 'text-green-500'; // < 30 min
    if (elapsedTime < 3600) return 'text-yellow-500'; // 30-60 min
    return 'text-red-500'; // > 60 min
  };

  if (!showTimer) {
    return (
      <button
        onClick={() => setShowTimer(true)}
        className="fixed top-4 left-1/2 -translate-x-1/2 p-2 bg-[#1c1c1e]/80 backdrop-blur-lg rounded-full hover:bg-[#2c2c2e] transition z-40"
        title="Show timer"
      >
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>
    );
  }

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 bg-[#1c1c1e]/90 backdrop-blur-lg rounded-full px-6 py-3 border border-[#2c2c2e] shadow-2xl z-40">
      <div className="flex items-center gap-3">
        <svg className={`w-5 h-5 ${getTimerColor()}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className={`font-mono text-lg font-semibold ${getTimerColor()}`}>
          {formatTime(elapsedTime)}
        </span>
        <button
          onClick={() => setShowTimer(false)}
          className="p-1 hover:bg-[#2c2c2e] rounded transition"
        >
          <svg className="w-4 h-4 text-[#8e8e93]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {elapsedTime >= 1800 && (
        <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs text-yellow-500 font-medium">
          {elapsedTime >= 3600 ? '⚠️ Long meeting!' : '⏰ 30+ minutes'}
        </div>
      )}
    </div>
  );
}
